// Code Injection Generator Utilities
// Functions to process templates and generate final code

import { 
  ComponentTemplate, 
  GlobalSettings, 
  SelectedComponents, 
  ComponentSelection,
  GeneratedCode, 
  ANIMATION_SPEEDS,
  ValidationResult,
  ConflictInfo,
  ComponentCategory,
  CATEGORY_INFO,
  ButtonShadowSettings
} from '@/types/codeInjection';
import { componentsByCategory, phoneValidatorScript } from '@/constants/codeInjectionTemplates';

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Generate CSS for button shadow/glow effects
 */
function generateShadowCSS(shadowSettings: ButtonShadowSettings): string {
  if (shadowSettings.type === 'none') {
    return 'box-shadow: none;';
  }
  
  const rgb = hexToRgb(shadowSettings.color);
  if (!rgb) {
    return 'box-shadow: none;';
  }
  
  const { r, g, b } = rgb;
  const opacity = shadowSettings.strength;
  
  if (shadowSettings.type === 'shadow') {
    // Traditional shadow - darker, positioned below
    const blur = Math.round(15 * shadowSettings.strength);
    const spread = Math.round(5 * shadowSettings.strength);
    const yOffset = Math.round(6 * shadowSettings.strength);
    return `box-shadow: 0 ${yOffset}px ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, ${opacity});`;
  } else if (shadowSettings.type === 'glow') {
    // Glow effect - softer, no offset
    const blur = Math.round(20 * shadowSettings.strength);
    const spread = Math.round(3 * shadowSettings.strength);
    return `box-shadow: 0 0 ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, ${opacity});`;
  }
  
  return 'box-shadow: none;';
}

/**
 * Process template string by replacing variables
 */
function processTemplate(template: string, globalSettings: GlobalSettings): string {
  let processedTemplate = template;
  
  // Replace animation duration
  const animationDuration = ANIMATION_SPEEDS[globalSettings.animationSpeed];
  processedTemplate = processedTemplate.replace(/{{animationDuration}}/g, animationDuration.toString());
  
  // Replace primary color
  processedTemplate = processedTemplate.replace(/{{primaryColor}}/g, globalSettings.primaryColor);
  
  // Replace secondary color
  processedTemplate = processedTemplate.replace(/{{secondaryColor}}/g, globalSettings.secondaryColor);
  
  // Replace primary color RGB
  const primaryRgb = hexToRgb(globalSettings.primaryColor);
  if (primaryRgb) {
    const primaryRgbString = `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`;
    processedTemplate = processedTemplate.replace(/{{primaryColorRgb}}/g, primaryRgbString);
  }
  
  // Replace secondary color RGB
  const secondaryRgb = hexToRgb(globalSettings.secondaryColor);
  if (secondaryRgb) {
    const secondaryRgbString = `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`;
    processedTemplate = processedTemplate.replace(/{{secondaryColorRgb}}/g, secondaryRgbString);
  }
  
  // Replace overlay strength
  const overlayStrength = globalSettings.overlayStrength || 0.3;
  processedTemplate = processedTemplate.replace(/{{overlayStrength}}/g, overlayStrength.toString());
  
  // Replace movement strength
  const movementStrength = globalSettings.movementStrength || 1.0;
  processedTemplate = processedTemplate.replace(/{{movementStrength}}/g, movementStrength.toString());
  
  // Replace glow strength
  const glowStrength = globalSettings.glowStrength || 1.0;
  processedTemplate = processedTemplate.replace(/{{glowStrength}}/g, glowStrength.toString());
  
  // Replace title color strength
  const titleColorStrength = globalSettings.titleColorStrength || 1.0;
  processedTemplate = processedTemplate.replace(/{{titleColorStrength}}/g, titleColorStrength.toString());
  
  // Replace description fade strength
  const descriptionFadeStrength = globalSettings.descriptionFadeStrength || 1.0;
  processedTemplate = processedTemplate.replace(/{{descriptionFadeStrength}}/g, descriptionFadeStrength.toString());
  
  // Replace title style strength
  const titleStyleStrength = globalSettings.titleStyleStrength || 1.0;
  processedTemplate = processedTemplate.replace(/{{titleStyleStrength}}/g, titleStyleStrength.toString());
  
  // Replace category header text color
  const categoryHeaderTextColor = globalSettings.categoryHeaderTextColor || '#f0f0f0';
  processedTemplate = processedTemplate.replace(/{{categoryHeaderTextColor}}/g, categoryHeaderTextColor);
  
  // Replace typography variables
  const fontWeight = globalSettings.fontWeight || 700;
  processedTemplate = processedTemplate.replace(/{{fontWeight}}/g, fontWeight.toString());
  
  const fontSize = globalSettings.fontSize || 28;
  processedTemplate = processedTemplate.replace(/{{fontSize}}/g, fontSize.toString());
  
  const fontStyle = globalSettings.fontStyle || 'normal';
  processedTemplate = processedTemplate.replace(/{{fontStyle}}/g, fontStyle);
  
  // Replace category header gradient colors
  const categoryHeaderGradientPrimary = globalSettings.categoryHeaderGradientPrimary || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{categoryHeaderGradientPrimary}}/g, categoryHeaderGradientPrimary);
  
  const categoryHeaderGradientSecondary = globalSettings.categoryHeaderGradientSecondary || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{categoryHeaderGradientSecondary}}/g, categoryHeaderGradientSecondary);
  
  // Replace category underline color variables
  const categoryUnderlineColor = globalSettings.categoryUnderlineColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{categoryUnderlineColor}}/g, categoryUnderlineColor);
  
  const categoryUnderlinePrimaryColor = globalSettings.categoryUnderlinePrimaryColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{categoryUnderlinePrimaryColor}}/g, categoryUnderlinePrimaryColor);
  
  const categoryUnderlineSecondaryColor = globalSettings.categoryUnderlineSecondaryColor || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{categoryUnderlineSecondaryColor}}/g, categoryUnderlineSecondaryColor);
  
  // Replace nav logo gradient colors
  const navLogoGradientPrimary = globalSettings.navLogoGradientPrimary || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{navLogoGradientPrimary}}/g, navLogoGradientPrimary);
  
  const navLogoGradientSecondary = globalSettings.navLogoGradientSecondary || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{navLogoGradientSecondary}}/g, navLogoGradientSecondary);
  
  // Replace nav logo glow color
  const navLogoGlowColor = globalSettings.navLogoGlowColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{navLogoGlowColor}}/g, navLogoGlowColor);
  
  // Convert nav logo glow color to RGB for rgba usage
  const navLogoGlowColorRgb = hexToRgb(navLogoGlowColor);
  if (navLogoGlowColorRgb) {
    const { r, g, b } = navLogoGlowColorRgb;
    processedTemplate = processedTemplate.replace(/{{navLogoGlowColorRgb}}/g, `${r}, ${g}, ${b}`);
  }
  
  // Replace search button styling variables
  const searchButtonRestBackgroundColor = globalSettings.searchButtonRestState?.backgroundColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{searchButtonRestBackgroundColor}}/g, searchButtonRestBackgroundColor);
  
  const searchButtonRestSvgColor = globalSettings.searchButtonRestState?.svgColor || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{searchButtonRestSvgColor}}/g, searchButtonRestSvgColor);
  
  const searchButtonHoverBackgroundColor = globalSettings.searchButtonHoverState?.backgroundColor || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{searchButtonHoverBackgroundColor}}/g, searchButtonHoverBackgroundColor);
  
  const searchButtonHoverSvgColor = globalSettings.searchButtonHoverState?.svgColor || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{searchButtonHoverSvgColor}}/g, searchButtonHoverSvgColor);
  
  // Convert search button background colors to RGB for rgba usage
  const searchButtonRestBackgroundColorRgb = hexToRgb(searchButtonRestBackgroundColor);
  if (searchButtonRestBackgroundColorRgb) {
    const { r, g, b } = searchButtonRestBackgroundColorRgb;
    processedTemplate = processedTemplate.replace(/{{searchButtonRestBackgroundColorRgb}}/g, `${r}, ${g}, ${b}`);
  }
  
  const searchButtonHoverBackgroundColorRgb = hexToRgb(searchButtonHoverBackgroundColor);
  if (searchButtonHoverBackgroundColorRgb) {
    const { r, g, b } = searchButtonHoverBackgroundColorRgb;
    processedTemplate = processedTemplate.replace(/{{searchButtonHoverBackgroundColorRgb}}/g, `${r}, ${g}, ${b}`);
  }
  
  // Replace menu item text styling variables
  if (globalSettings.menuItemTextRest) {
    const restStyle = globalSettings.menuItemTextRest;
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestColor}}/g, restStyle.color);
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestFontSize}}/g, restStyle.fontSize.toString());
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestFontWeight}}/g, restStyle.fontWeight.toString());
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestFontStyle}}/g, restStyle.fontStyle);
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestTextTransform}}/g, restStyle.textTransform);
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestLetterSpacing}}/g, restStyle.letterSpacing.toString());
    processedTemplate = processedTemplate.replace(/{{menuItemTextRestTextShadow}}/g, restStyle.textShadow);
  }
  
  if (globalSettings.menuItemTextHover) {
    const hoverStyle = globalSettings.menuItemTextHover;
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverColor}}/g, hoverStyle.color);
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverFontSize}}/g, hoverStyle.fontSize.toString());
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverFontWeight}}/g, hoverStyle.fontWeight.toString());
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverFontStyle}}/g, hoverStyle.fontStyle);
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverTextTransform}}/g, hoverStyle.textTransform);
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverLetterSpacing}}/g, hoverStyle.letterSpacing.toString());
    processedTemplate = processedTemplate.replace(/{{menuItemTextHoverTextShadow}}/g, hoverStyle.textShadow);
  }
  
  // Replace button style variables
  if (globalSettings.buttonRestState) {
    const restStyle = globalSettings.buttonRestState;
    processedTemplate = processedTemplate.replace(/{{buttonRestBg}}/g, restStyle.backgroundColor);
    processedTemplate = processedTemplate.replace(/{{buttonRestText}}/g, restStyle.textColor);
    processedTemplate = processedTemplate.replace(/{{buttonRestBorder}}/g, restStyle.borderColor);
    processedTemplate = processedTemplate.replace(/{{buttonRestTextTransform}}/g, restStyle.textTransform);
  }
  
  if (globalSettings.buttonHoverState) {
    const hoverStyle = globalSettings.buttonHoverState;
    processedTemplate = processedTemplate.replace(/{{buttonHoverBg}}/g, hoverStyle.backgroundColor);
    processedTemplate = processedTemplate.replace(/{{buttonHoverText}}/g, hoverStyle.textColor);
    processedTemplate = processedTemplate.replace(/{{buttonHoverBorder}}/g, hoverStyle.borderColor);
    processedTemplate = processedTemplate.replace(/{{buttonHoverTextTransform}}/g, hoverStyle.textTransform);
  }
  
  // Replace button shadow variables
  if (globalSettings.buttonShadowRest) {
    const shadowRest = globalSettings.buttonShadowRest;
    const restShadowCSS = generateShadowCSS(shadowRest);
    processedTemplate = processedTemplate.replace(/{{buttonShadowRest}}/g, restShadowCSS);
  } else {
    processedTemplate = processedTemplate.replace(/{{buttonShadowRest}}/g, '');
  }
  
  if (globalSettings.buttonShadowHover) {
    const shadowHover = globalSettings.buttonShadowHover;
    const hoverShadowCSS = generateShadowCSS(shadowHover);
    processedTemplate = processedTemplate.replace(/{{buttonShadowHover}}/g, hoverShadowCSS);
  } else {
    processedTemplate = processedTemplate.replace(/{{buttonShadowHover}}/g, '');
  }
  
  // Replace navigation style variables
  const navMainBg = globalSettings.navMainBg || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{navMainBg}}/g, navMainBg);
  
  const navMainBorder = globalSettings.navMainBorder || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{navMainBorder}}/g, navMainBorder);
  
  const navTopBg = globalSettings.navTopBg || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{navTopBg}}/g, navTopBg);
  
  const navTopBorder = globalSettings.navTopBorder || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{navTopBorder}}/g, navTopBorder);
  
  const navTopText = globalSettings.navTopText || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{navTopText}}/g, navTopText);
  
  const navBottomBg = globalSettings.navBottomBg || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{navBottomBg}}/g, navBottomBg);
  
  const navBottomBorder = globalSettings.navBottomBorder || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{navBottomBorder}}/g, navBottomBorder);
  
  const navLogoText = globalSettings.navLogoText || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{navLogoText}}/g, navLogoText);
  
  const navSliderBg = globalSettings.navSliderBg || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{navSliderBg}}/g, navSliderBg);
  
  const navSliderText = globalSettings.navSliderText || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{navSliderText}}/g, navSliderText);
  
  const navSearchButtonBg = globalSettings.navSearchButtonBg || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{navSearchButtonBg}}/g, navSearchButtonBg);
  
  const navSearchButtonText = globalSettings.navSearchButtonText || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{navSearchButtonText}}/g, navSearchButtonText);
  
  // Replace price tag colors
  const priceTagBackgroundColor = globalSettings.priceTagBackgroundColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{priceTagBackgroundColor}}/g, priceTagBackgroundColor);
  
  const priceTagTextColor = globalSettings.priceTagTextColor || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{priceTagTextColor}}/g, priceTagTextColor);
  
  // Replace price tag gradient settings
  const priceTagGradientDirection = globalSettings.priceTagGradientDirection || '135deg';
  processedTemplate = processedTemplate.replace(/{{priceTagGradientDirection}}/g, priceTagGradientDirection);
  
  const priceTagGradientStartColor = globalSettings.priceTagGradientStartColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{priceTagGradientStartColor}}/g, priceTagGradientStartColor);
  
  const priceTagGradientEndColor = globalSettings.priceTagGradientEndColor || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{priceTagGradientEndColor}}/g, priceTagGradientEndColor);
  
  const priceTagGradientTextColor = globalSettings.priceTagGradientTextColor || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{priceTagGradientTextColor}}/g, priceTagGradientTextColor);
  
  // Replace welcome message variables
  const welcomeMessageMorningText = globalSettings.welcomeMessageMorningText || 'Good morning!';
  processedTemplate = processedTemplate.replace(/{{welcomeMessageMorningText}}/g, welcomeMessageMorningText);
  
  const welcomeMessageAfternoonText = globalSettings.welcomeMessageAfternoonText || 'Good afternoon!';
  processedTemplate = processedTemplate.replace(/{{welcomeMessageAfternoonText}}/g, welcomeMessageAfternoonText);
  
  const welcomeMessageEveningText = globalSettings.welcomeMessageEveningText || 'Good evening!';
  processedTemplate = processedTemplate.replace(/{{welcomeMessageEveningText}}/g, welcomeMessageEveningText);
  
  const welcomeMessageSecondaryText = globalSettings.welcomeMessageSecondaryText || 'Welcome to {restaurant name}!';
  // Replace {restaurant name} placeholder with actual restaurant name from globalSettings
  const restaurantName = globalSettings.restaurantName || 'our restaurant';
  const processedSecondaryText = welcomeMessageSecondaryText.replace('{restaurant name}', restaurantName);
  processedTemplate = processedTemplate.replace(/{{welcomeMessageSecondaryText}}/g, processedSecondaryText);
  
  const welcomeMessageMorningHour = globalSettings.welcomeMessageMorningHour || 12;
  processedTemplate = processedTemplate.replace(/{{welcomeMessageMorningHour}}/g, welcomeMessageMorningHour.toString());
  
  const welcomeMessageAfternoonHour = globalSettings.welcomeMessageAfternoonHour || 17;
  processedTemplate = processedTemplate.replace(/{{welcomeMessageAfternoonHour}}/g, welcomeMessageAfternoonHour.toString());
  
  const welcomeMessageEveningHour = globalSettings.welcomeMessageEveningHour || 24;
  processedTemplate = processedTemplate.replace(/{{welcomeMessageEveningHour}}/g, welcomeMessageEveningHour.toString());
  
  const welcomeMessageTextColor = globalSettings.welcomeMessageTextColor || '#ffffff';
  processedTemplate = processedTemplate.replace(/{{welcomeMessageTextColor}}/g, welcomeMessageTextColor);
  
  const welcomeMessageGradientStartColor = globalSettings.welcomeMessageGradientStartColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{welcomeMessageGradientStartColor}}/g, welcomeMessageGradientStartColor);
  
  const welcomeMessageGradientEndColor = globalSettings.welcomeMessageGradientEndColor || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{welcomeMessageGradientEndColor}}/g, welcomeMessageGradientEndColor);
  
  const welcomeMessageBorderColor = globalSettings.welcomeMessageBorderColor || 'transparent';
  processedTemplate = processedTemplate.replace(/{{welcomeMessageBorderColor}}/g, welcomeMessageBorderColor);
  
  // Replace welcome message border style (conditional based on transparency)
  const welcomeMessageBorderStyle = welcomeMessageBorderColor === 'transparent' ? 
    '' : `greetingDiv.style.border = '2px solid ${welcomeMessageBorderColor}';`;
  processedTemplate = processedTemplate.replace(/{{welcomeMessageBorderStyle}}/g, welcomeMessageBorderStyle);
  
  // Replace general restaurant name variable
  // Note: restaurantName was already defined above for welcome message processing
  processedTemplate = processedTemplate.replace(/{{restaurantName}}/g, restaurantName);

  // Replace enhanced tag badge colors (applies to all tags)
  const tagBadgeEnhancedPrimaryColor = globalSettings.tagBadgeEnhancedPrimaryColor || globalSettings.primaryColor;
  processedTemplate = processedTemplate.replace(/{{tagBadgeEnhancedPrimaryColor}}/g, tagBadgeEnhancedPrimaryColor);

  const tagBadgeEnhancedSecondaryColor = globalSettings.tagBadgeEnhancedSecondaryColor || globalSettings.secondaryColor;
  processedTemplate = processedTemplate.replace(/{{tagBadgeEnhancedSecondaryColor}}/g, tagBadgeEnhancedSecondaryColor);

  // Convert enhanced tag badge colors to RGB for rgba usage
  const tagBadgeEnhancedPrimaryRgb = hexToRgb(tagBadgeEnhancedPrimaryColor);
  if (tagBadgeEnhancedPrimaryRgb) {
    const { r, g, b } = tagBadgeEnhancedPrimaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeEnhancedPrimaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeEnhancedSecondaryRgb = hexToRgb(tagBadgeEnhancedSecondaryColor);
  if (tagBadgeEnhancedSecondaryRgb) {
    const { r, g, b } = tagBadgeEnhancedSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeEnhancedSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  // Replace special dietary tag badge colors
  const tagBadgeVeganPrimaryColor = globalSettings.tagBadgeVeganPrimaryColor || '#26c526';
  processedTemplate = processedTemplate.replace(/{{tagBadgeVeganPrimaryColor}}/g, tagBadgeVeganPrimaryColor);

  const tagBadgeVeganSecondaryColor = globalSettings.tagBadgeVeganSecondaryColor || '#166a16';
  processedTemplate = processedTemplate.replace(/{{tagBadgeVeganSecondaryColor}}/g, tagBadgeVeganSecondaryColor);

  const tagBadgeVegetarianPrimaryColor = globalSettings.tagBadgeVegetarianPrimaryColor || '#90EE90';
  processedTemplate = processedTemplate.replace(/{{tagBadgeVegetarianPrimaryColor}}/g, tagBadgeVegetarianPrimaryColor);

  const tagBadgeVegetarianSecondaryColor = globalSettings.tagBadgeVegetarianSecondaryColor || '#32CD32';
  processedTemplate = processedTemplate.replace(/{{tagBadgeVegetarianSecondaryColor}}/g, tagBadgeVegetarianSecondaryColor);

  const tagBadgeGlutenFreePrimaryColor = globalSettings.tagBadgeGlutenFreePrimaryColor || '#FFB347';
  processedTemplate = processedTemplate.replace(/{{tagBadgeGlutenFreePrimaryColor}}/g, tagBadgeGlutenFreePrimaryColor);

  const tagBadgeGlutenFreeSecondaryColor = globalSettings.tagBadgeGlutenFreeSecondaryColor || '#FF8C00';
  processedTemplate = processedTemplate.replace(/{{tagBadgeGlutenFreeSecondaryColor}}/g, tagBadgeGlutenFreeSecondaryColor);

  const tagBadgeSpicyPrimaryColor = globalSettings.tagBadgeSpicyPrimaryColor || '#FF6B6B';
  processedTemplate = processedTemplate.replace(/{{tagBadgeSpicyPrimaryColor}}/g, tagBadgeSpicyPrimaryColor);

  const tagBadgeSpicySecondaryColor = globalSettings.tagBadgeSpicySecondaryColor || '#FF3333';
  processedTemplate = processedTemplate.replace(/{{tagBadgeSpicySecondaryColor}}/g, tagBadgeSpicySecondaryColor);

  const tagBadgeDairyFreePrimaryColor = globalSettings.tagBadgeDairyFreePrimaryColor || '#87CEEB';
  processedTemplate = processedTemplate.replace(/{{tagBadgeDairyFreePrimaryColor}}/g, tagBadgeDairyFreePrimaryColor);

  const tagBadgeDairyFreeSecondaryColor = globalSettings.tagBadgeDairyFreeSecondaryColor || '#4682B4';
  processedTemplate = processedTemplate.replace(/{{tagBadgeDairyFreeSecondaryColor}}/g, tagBadgeDairyFreeSecondaryColor);

  const tagBadgeNutFreePrimaryColor = globalSettings.tagBadgeNutFreePrimaryColor || '#DEB887';
  processedTemplate = processedTemplate.replace(/{{tagBadgeNutFreePrimaryColor}}/g, tagBadgeNutFreePrimaryColor);

  const tagBadgeNutFreeSecondaryColor = globalSettings.tagBadgeNutFreeSecondaryColor || '#8B7355';
  processedTemplate = processedTemplate.replace(/{{tagBadgeNutFreeSecondaryColor}}/g, tagBadgeNutFreeSecondaryColor);

  const tagBadgeHalalPrimaryColor = globalSettings.tagBadgeHalalPrimaryColor || '#019000';
  processedTemplate = processedTemplate.replace(/{{tagBadgeHalalPrimaryColor}}/g, tagBadgeHalalPrimaryColor);

  const tagBadgeHalalSecondaryColor = globalSettings.tagBadgeHalalSecondaryColor || '#B8860B';
  processedTemplate = processedTemplate.replace(/{{tagBadgeHalalSecondaryColor}}/g, tagBadgeHalalSecondaryColor);

  const tagBadgePopularPrimaryColor = globalSettings.tagBadgePopularPrimaryColor || '#b400fa';
  processedTemplate = processedTemplate.replace(/{{tagBadgePopularPrimaryColor}}/g, tagBadgePopularPrimaryColor);

  const tagBadgePopularSecondaryColor = globalSettings.tagBadgePopularSecondaryColor || '#ff0000';
  processedTemplate = processedTemplate.replace(/{{tagBadgePopularSecondaryColor}}/g, tagBadgePopularSecondaryColor);

  const tagBadgeNewPrimaryColor = globalSettings.tagBadgeNewPrimaryColor || '#ff0000';
  processedTemplate = processedTemplate.replace(/{{tagBadgeNewPrimaryColor}}/g, tagBadgeNewPrimaryColor);

  const tagBadgeNewSecondaryColor = globalSettings.tagBadgeNewSecondaryColor || '#3f92ff';
  processedTemplate = processedTemplate.replace(/{{tagBadgeNewSecondaryColor}}/g, tagBadgeNewSecondaryColor);

  const tagBadgeDealPrimaryColor = globalSettings.tagBadgeDealPrimaryColor || '#4fc060';
  processedTemplate = processedTemplate.replace(/{{tagBadgeDealPrimaryColor}}/g, tagBadgeDealPrimaryColor);

  const tagBadgeDealSecondaryColor = globalSettings.tagBadgeDealSecondaryColor || '#ff0000';
  processedTemplate = processedTemplate.replace(/{{tagBadgeDealSecondaryColor}}/g, tagBadgeDealSecondaryColor);

  // Convert tag badge colors to RGB for rgba usage (for shadow effects)
  const tagBadgeVeganPrimaryRgb = hexToRgb(tagBadgeVeganPrimaryColor);
  if (tagBadgeVeganPrimaryRgb) {
    const { r, g, b } = tagBadgeVeganPrimaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeVeganPrimaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeVegetarianPrimaryRgb = hexToRgb(tagBadgeVegetarianPrimaryColor);
  if (tagBadgeVegetarianPrimaryRgb) {
    const { r, g, b } = tagBadgeVegetarianPrimaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeVegetarianPrimaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeVegetarianSecondaryRgb = hexToRgb(tagBadgeVegetarianSecondaryColor);
  if (tagBadgeVegetarianSecondaryRgb) {
    const { r, g, b } = tagBadgeVegetarianSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeVegetarianSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeGlutenFreeSecondaryRgb = hexToRgb(tagBadgeGlutenFreeSecondaryColor);
  if (tagBadgeGlutenFreeSecondaryRgb) {
    const { r, g, b } = tagBadgeGlutenFreeSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeGlutenFreeSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeSpicySecondaryRgb = hexToRgb(tagBadgeSpicySecondaryColor);
  if (tagBadgeSpicySecondaryRgb) {
    const { r, g, b } = tagBadgeSpicySecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeSpicySecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeDairyFreeSecondaryRgb = hexToRgb(tagBadgeDairyFreeSecondaryColor);
  if (tagBadgeDairyFreeSecondaryRgb) {
    const { r, g, b } = tagBadgeDairyFreeSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeDairyFreeSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeNutFreeSecondaryRgb = hexToRgb(tagBadgeNutFreeSecondaryColor);
  if (tagBadgeNutFreeSecondaryRgb) {
    const { r, g, b } = tagBadgeNutFreeSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeNutFreeSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeHalalSecondaryRgb = hexToRgb(tagBadgeHalalSecondaryColor);
  if (tagBadgeHalalSecondaryRgb) {
    const { r, g, b } = tagBadgeHalalSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeHalalSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgePopularSecondaryRgb = hexToRgb(tagBadgePopularSecondaryColor);
  if (tagBadgePopularSecondaryRgb) {
    const { r, g, b } = tagBadgePopularSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgePopularSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeNewSecondaryRgb = hexToRgb(tagBadgeNewSecondaryColor);
  if (tagBadgeNewSecondaryRgb) {
    const { r, g, b } = tagBadgeNewSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeNewSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  const tagBadgeDealSecondaryRgb = hexToRgb(tagBadgeDealSecondaryColor);
  if (tagBadgeDealSecondaryRgb) {
    const { r, g, b } = tagBadgeDealSecondaryRgb;
    processedTemplate = processedTemplate.replace(/{{tagBadgeDealSecondaryRgb}}/g, `${r}, ${g}, ${b}`);
  }

  // Add Premium Service Choice color mode aware styles
  const isLightMode = globalSettings.colorMode === 'light';
  
  // Service choice background
  const serviceChoiceBackground = isLightMode 
    ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.05) 100%)`
    : `linear-gradient(135deg, rgba(45, 45, 45, 0.9) 0%, rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.05) 100%)`;
  processedTemplate = processedTemplate.replace(/{{serviceChoiceBackground}}/g, serviceChoiceBackground);
  
  // Service choice hover background - keep white/clean background in light mode
  const serviceChoiceHoverBackground = isLightMode
    ? `linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.02) 100%)`
    : `linear-gradient(135deg, rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.15) 0%, rgba(${secondaryRgb ? `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}` : '165, 148, 86'}, 0.05) 100%)`;
  processedTemplate = processedTemplate.replace(/{{serviceChoiceHoverBackground}}/g, serviceChoiceHoverBackground);
  
  // Service choice shadow
  const serviceChoiceShadow = isLightMode
    ? '0 4px 12px rgba(0, 0, 0, 0.08)'
    : '0 6px 20px rgba(0, 0, 0, 0.2)';
  processedTemplate = processedTemplate.replace(/{{serviceChoiceShadow}}/g, serviceChoiceShadow);
  
  // Service choice hover shadow
  const serviceChoiceHoverShadow = isLightMode
    ? `0 8px 20px rgba(0, 0, 0, 0.12), 0 0 20px rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.25)`
    : `0 12px 30px rgba(0, 0, 0, 0.3), 0 0 25px rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.4)`;
  processedTemplate = processedTemplate.replace(/{{serviceChoiceHoverShadow}}/g, serviceChoiceHoverShadow);
  
  // Service choice icon color
  const serviceChoiceIconColor = isLightMode ? '#323232' : '#f0f0f0';
  processedTemplate = processedTemplate.replace(/{{serviceChoiceIconColor}}/g, serviceChoiceIconColor);
  
  // Modal background
  const modalBackground = isLightMode
    ? 'rgba(255, 255, 255, 0.98)'
    : 'rgba(30, 30, 30, 0.95)';
  processedTemplate = processedTemplate.replace(/{{modalBackground}}/g, modalBackground);
  
  // Modal shadow
  const modalShadow = isLightMode
    ? '0 15px 35px rgba(0, 0, 0, 0.15)'
    : '0 20px 50px rgba(0, 0, 0, 0.4)';
  processedTemplate = processedTemplate.replace(/{{modalShadow}}/g, modalShadow);
  
  // Comprehensive Service Choice variables
  // Service background
  const comprehensiveServiceBackground = isLightMode
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(45, 45, 45, 0.8)';
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceBackground}}/g, comprehensiveServiceBackground);
  
  // Service hover background - keep white in light mode
  const comprehensiveServiceHoverBackground = isLightMode
    ? 'rgba(255, 255, 255, 0.98)'
    : `rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.1)`;
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceHoverBackground}}/g, comprehensiveServiceHoverBackground);
  
  // Service shadow
  const comprehensiveServiceShadow = isLightMode
    ? '0 3px 8px rgba(0, 0, 0, 0.08)'
    : '0 4px 10px rgba(0, 0, 0, 0.15)';
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceShadow}}/g, comprehensiveServiceShadow);
  
  // Service hover shadow
  const comprehensiveServiceHoverShadow = isLightMode
    ? `0 6px 15px rgba(0, 0, 0, 0.1), 0 0 12px rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.2)`
    : `0 8px 20px rgba(0, 0, 0, 0.2), 0 0 15px rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.3)`;
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceHoverShadow}}/g, comprehensiveServiceHoverShadow);
  
  // Service icon color
  const comprehensiveServiceIconColor = isLightMode ? '#323232' : '#f0f0f0';
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceIconColor}}/g, comprehensiveServiceIconColor);
  
  // Modal background (same as Premium)
  const comprehensiveModalBackground = isLightMode
    ? 'rgba(255, 255, 255, 0.98)'
    : 'transparent';  // Use transparent for dark mode to maintain existing blur effect
  processedTemplate = processedTemplate.replace(/{{comprehensiveModalBackground}}/g, comprehensiveModalBackground);
  
  // Modal shadow
  const comprehensiveModalShadow = isLightMode
    ? '0 10px 25px rgba(0, 0, 0, 0.12)'
    : '0 10px 30px rgba(0, 0, 0, 0.3)';
  processedTemplate = processedTemplate.replace(/{{comprehensiveModalShadow}}/g, comprehensiveModalShadow);
  
  // Service header backgrounds
  const comprehensiveServiceHeaderBackground = isLightMode
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(45, 45, 45, 0.6)';
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceHeaderBackground}}/g, comprehensiveServiceHeaderBackground);
  
  const comprehensiveServiceHeaderHoverBackground = isLightMode
    ? `rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.05)`
    : `rgba(${primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '197, 176, 104'}, 0.15)`;
  processedTemplate = processedTemplate.replace(/{{comprehensiveServiceHeaderHoverBackground}}/g, comprehensiveServiceHeaderHoverBackground);
  
  return processedTemplate;
}

/**
 * Get selected component templates - handles both single selections and arrays, including nested sub-templates
 */
function getSelectedTemplates(selectedComponents: SelectedComponents): ComponentTemplate[] {
  const templates: ComponentTemplate[] = [];
  
  Object.entries(selectedComponents).forEach(([category, componentSelection]) => {
    if (componentSelection) {
      const categoryTemplates = componentsByCategory[category as keyof typeof componentsByCategory];
      
      // Handle both single string and array selections
      const componentIds = Array.isArray(componentSelection) ? componentSelection : [componentSelection];
      
      componentIds.forEach(componentId => {
        if (typeof componentId === 'string') {
          // Simple string-based selection
          const template = categoryTemplates?.find(t => t.id === componentId);
          if (template) {
            templates.push(template);
          }
        } else if (typeof componentId === 'object' && componentId.id) {
          // Object-based selection with potential sub-template
          const mainTemplate = categoryTemplates?.find(t => t.id === componentId.id);
          if (mainTemplate) {
            if (componentId.subTemplate && mainTemplate.subTemplates) {
              // Find the specific sub-template
              const subTemplate = mainTemplate.subTemplates.find(st => st.id === componentId.subTemplate);
              if (subTemplate) {
                // Create a synthetic template from the sub-template
                const syntheticTemplate: ComponentTemplate = {
                  id: subTemplate.id,
                  category: mainTemplate.category,
                  name: `${mainTemplate.name} - ${subTemplate.name}`,
                  description: subTemplate.description,
                  cssTemplate: subTemplate.cssTemplate,
                  jsTemplate: subTemplate.jsTemplate,
                  variables: subTemplate.variables
                };
                templates.push(syntheticTemplate);
              }
            } else {
              // No sub-template specified, use the main template
              templates.push(mainTemplate);
            }
          }
        }
      });
    }
  });
  
  return templates;
}

/**
 * Generate base menu item template that's always included
 */
function generateBaseMenuItemTemplate(globalSettings: GlobalSettings): ComponentTemplate {
  return {
    id: 'base-menu-item-styles',
    category: 'base' as ComponentCategory,
    name: 'Base Menu Item Styles',
    description: 'Essential styling for menu item cards - always included',
    cssTemplate: `
      /* Base card styling with enhanced appearance */
      .item__DishComponent-wkeq8p-0 {
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.5s ease;
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
        transform: translateY(0);
        position: relative;
        margin-bottom: 20px;
      }
      
      /* Image container background to prevent flashing */
      .item__Image-wkeq8p-1 {
        background-color: #2d2d2d;
        position: relative;
        overflow: hidden;
      }
      
      /* Enhanced content padding */
      .item__Content-wkeq8p-4 {
        padding: 25px 22px 30px 22px !important;
      }
    `,
    variables: {
      primaryColor: '{{primaryColor}}',
      secondaryColor: '{{secondaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      secondaryColorRgb: '{{secondaryColorRgb}}'
    }
  };
}

/**
 * Generate head section code
 */
function generateHeadCode(templates: ComponentTemplate[], globalSettings: GlobalSettings): string {
  const headParts: string[] = [];
  
  // Always include Google Fonts and Animate.css
  headParts.push('<!-- Enhanced typography with Gabarito font -->');
  headParts.push('<link href="https://fonts.googleapis.com/css2?family=Gabarito:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">');
  headParts.push('');
  headParts.push('<!-- Animate.css for smooth animations -->');
  headParts.push('<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css" />');
  headParts.push('');
  
  // Collect all CSS templates
  const cssTemplates: string[] = [];
  
  // Add base styles
  let baseStyles = `
  /* Base Enhancements */
  body, html {
    overflow-x: hidden;
    scroll-behavior: smooth;
  }
  
  /* Enhanced Typography */
  h1, h2, h3, h4, h5 {
    font-weight: 700;
    letter-spacing: 0.5px;
  }`;

  // Add light mode specific styles
  if (globalSettings.colorMode === 'light') {
    baseStyles += `

  /* Fix top bar styling to match header (Light Mode) */
  .top__Wrapper-sc-1amtn2l-0 {
    background: #FFFBF2 !important;
    backdrop-filter: blur(5px);
    border-bottom: 1px solid rgba(227, 78, 33, 0.1);
  }`;
  }

  baseStyles += `
    
  p {
    line-height: 1.6;
  }`;

  cssTemplates.push(baseStyles);
  
  // Add component CSS
  templates.forEach(template => {
    if (template.cssTemplate) {
      cssTemplates.push(processTemplate(template.cssTemplate, globalSettings));
    }
  });
  
  // Wrap CSS in style tags
  if (cssTemplates.length > 0) {
    headParts.push('<style>');
    headParts.push(cssTemplates.join('\n'));
    headParts.push('</style>');
  }
  
  return headParts.join('\n');
}

/**
 * Generate body section code
 */
function generateBodyCode(templates: ComponentTemplate[], globalSettings: GlobalSettings): string {
  const bodyParts: string[] = [];
  
  // Collect all JavaScript templates
  const jsTemplates: string[] = [];
  
  templates.forEach(template => {
    if (template.jsTemplate) {
      jsTemplates.push(processTemplate(template.jsTemplate, globalSettings));
    }
  });
  
  // Wrap JavaScript in script tags if any exists
  if (jsTemplates.length > 0) {
    bodyParts.push('<script>');
    bodyParts.push('document.addEventListener(\'DOMContentLoaded\', function() {');
    bodyParts.push(jsTemplates.join('\n\n'));
    bodyParts.push('});');
    bodyParts.push('</script>');
    bodyParts.push('');
  }
  
  // Always append phone validator script
  bodyParts.push('<!-- Phone Validation Script -->');
  bodyParts.push(phoneValidatorScript);
  
  return bodyParts.join('\n');
}

/**
 * Calculate code statistics
 */
function calculateStats(headCode: string, bodyCode: string, componentCount: number): GeneratedCode['stats'] {
  return {
    headSize: new Blob([headCode]).size,
    bodySize: new Blob([bodyCode]).size,
    totalComponents: componentCount
  };
}

/**
 * Main code generation function
 */
export function generateCode(
  selectedComponents: SelectedComponents, 
  globalSettings: GlobalSettings
): GeneratedCode {
  // Always include base template first, then selected templates
  const baseTemplate = generateBaseMenuItemTemplate(globalSettings);
  const selectedTemplates = getSelectedTemplates(selectedComponents);
  const templates = [baseTemplate, ...selectedTemplates];
  
  // Generate head and body code
  const headCode = generateHeadCode(templates, globalSettings);
  const bodyCode = generateBodyCode(templates, globalSettings);
  
  // Calculate stats
  const stats = calculateStats(headCode, bodyCode, templates.length);
  
  return {
    headCode,
    bodyCode,
    stats
  };
}

/**
 * Format code size for display
 */
export function formatCodeSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} bytes`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

/**
 * Get component template by ID
 */
export function getComponentTemplate(componentId: string): ComponentTemplate | null {
  for (const categoryTemplates of Object.values(componentsByCategory)) {
    const template = categoryTemplates.find(t => t.id === componentId);
    if (template) {
      return template;
    }
  }
  return null;
}

/**
 * Validate selected components - handles both single selections and arrays
 */
export function validateSelectedComponents(selectedComponents: SelectedComponents): boolean {
  // Check if at least one component is selected
  const hasSelection = Object.values(selectedComponents).some(selection => {
    if (Array.isArray(selection)) {
      return selection.length > 0;
    }
    return selection !== null;
  });
  
  if (!hasSelection) {
    return false;
  }
  
  // Check if all selected components exist
  for (const [category, componentSelection] of Object.entries(selectedComponents)) {
    if (componentSelection) {
      const componentIds = Array.isArray(componentSelection) ? componentSelection : [componentSelection];
      
      for (const componentId of componentIds) {
        // Handle both string IDs and ComponentSelection objects
        const actualId = typeof componentId === 'string' ? componentId : componentId.id;
        const template = getComponentTemplate(actualId);
        if (!template) {
          console.warn(`Component template not found: ${actualId}`);
          return false;
        }
      }
    }
  }
  
  return true;
}

/**
 * Get preview description for selected components
 */
export function getPreviewDescription(selectedComponents: SelectedComponents): string {
  const templates = getSelectedTemplates(selectedComponents);
  
  if (templates.length === 0) {
    return 'No components selected';
  }
  
  const descriptions = templates.map(t => t.name);
  
  if (descriptions.length === 1) {
    return descriptions[0];
  } else if (descriptions.length === 2) {
    return `${descriptions[0]} and ${descriptions[1]}`;
  } else {
    return `${descriptions.slice(0, -1).join(', ')}, and ${descriptions.slice(-1)[0]}`;
  }
}

/**
 * Export configuration as JSON
 */
export function exportConfiguration(
  selectedComponents: SelectedComponents,
  globalSettings: GlobalSettings
): string {
  const config = {
    selectedComponents,
    globalSettings,
    timestamp: new Date().toISOString(),
    version: '1.0.0'
  };
  
  return JSON.stringify(config, null, 2);
}

/**
 * Import configuration from JSON
 */
export function importConfiguration(jsonString: string): {
  selectedComponents: SelectedComponents;
  globalSettings: GlobalSettings;
} | null {
  try {
    const config = JSON.parse(jsonString);
    
    // Validate structure
    if (!config.selectedComponents || !config.globalSettings) {
      throw new Error('Invalid configuration structure');
    }
    
    return {
      selectedComponents: config.selectedComponents,
      globalSettings: config.globalSettings
    };
  } catch (error) {
    console.error('Failed to import configuration:', error);
    return null;
  }
}

/**
 * Enhanced validation with conflict detection
 */
export function validateSelectedComponentsEnhanced(selectedComponents: SelectedComponents): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: [],
    conflicts: []
  };

  // Check if at least one component is selected
  const hasSelection = Object.values(selectedComponents).some(selection => {
    if (Array.isArray(selection)) {
      return selection.length > 0;
    }
    return selection !== null;
  });

  if (!hasSelection) {
    // No error when no components selected - just return valid with empty state
    return result;
  }

  // Check if all selected components exist and detect conflicts
  for (const [category, componentSelection] of Object.entries(selectedComponents)) {
    if (componentSelection) {
      const componentIds = Array.isArray(componentSelection) ? componentSelection : [componentSelection];
      const categoryInfo = CATEGORY_INFO[category as ComponentCategory];
      
      // Check if multiple selections are allowed for this category
      if (componentIds.length > 1 && !categoryInfo?.allowMultiple) {
        result.isValid = false;
        result.errors.push(`Category '${categoryInfo?.name || category}' does not support multiple selections.`);
        continue;
      }
      
      // Validate each component and check for conflicts
      for (const componentId of componentIds) {
        // Handle both string IDs and ComponentSelection objects
        const actualId = typeof componentId === 'string' ? componentId : componentId.id;
        const template = getComponentTemplate(actualId);
        if (!template) {
          result.isValid = false;
          result.errors.push(`Component template not found: ${actualId}`);
          continue;
        }

        // Check for conflicts with other selected components
        if (template.conflicts) {
          const conflicts = template.conflicts.filter(conflictId => 
            componentIds.some(id => (typeof id === 'string' ? id : id.id) === conflictId)
          );
          
          if (conflicts.length > 0) {
            result.isValid = false;
            const conflictInfo: ConflictInfo = {
              componentId: template.id,
              conflictsWith: conflicts,
              category: category as ComponentCategory,
              severity: 'error'
            };
            result.conflicts?.push(conflictInfo);
            result.errors.push(`Component '${template.name}' conflicts with: ${conflicts.map(id => getComponentTemplate(id)?.name || id).join(', ')}`);
          }
        }
      }
    }
  }

  return result;
}

/**
 * Check if component selection is valid for category
 */
export function isValidSelectionForCategory(
  category: ComponentCategory, 
  selection: string | string[] | (string | ComponentSelection)[]
): boolean {
  const categoryInfo = CATEGORY_INFO[category];
  
  if (Array.isArray(selection)) {
    // Multiple selection - check if allowed
    if (!categoryInfo?.allowMultiple) {
      return false;
    }
    
    // Check for conflicts within the selection
    for (let i = 0; i < selection.length; i++) {
      // Handle both string IDs and ComponentSelection objects
      const actualId = typeof selection[i] === 'string' ? selection[i] as string : (selection[i] as ComponentSelection).id;
      const template = getComponentTemplate(actualId);
      if (template?.conflicts) {
        const hasConflict = template.conflicts.some(conflictId => 
          selection.some(item => (typeof item === 'string' ? item : item.id) === conflictId)
        );
        if (hasConflict) {
          return false;
        }
      }
    }
  }
  
  return true;
}

/**
 * Get component conflicts for a selection
 */
export function getComponentConflicts(componentId: string, selectedComponents: SelectedComponents): string[] {
  const template = getComponentTemplate(componentId);
  if (!template?.conflicts) {
    return [];
  }

  const conflicts: string[] = [];
  
  for (const [category, selection] of Object.entries(selectedComponents)) {
    if (selection) {
      const componentIds = Array.isArray(selection) ? selection : [selection];
      
      for (const selectedId of componentIds) {
        // Handle both string IDs and ComponentSelection objects
        const actualId = typeof selectedId === 'string' ? selectedId : selectedId.id;
        if (template.conflicts.includes(actualId)) {
          conflicts.push(actualId);
        }
      }
    }
  }
  
  return conflicts;
}